"use client";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Physics, useSphere } from '@react-three/cannon';
import { 
  Instagram, Linkedin, Send, Palette, 
  Cpu, MousePointer2, Wind, ChevronRight 
} from 'lucide-react';

// --- 1. PARAMETRIC PAPER KITE WIREFRAME GEOMETRY ---
const KiteGeometry = () => {
  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    
    const vertices = new Float32Array([
       0,  1.2,  0,     // 0: Top tip
      -0.7, 0.2, 0.2,   // 1: Left wing tip
       0, -1.2,  0,     // 2: Bottom spine tip
       0.7, 0.2, 0.2,   // 3: Right wing tip
       0,  0.2, -0.3,   // 4: Back fold indentation
    ]);

    const indices = [
      0, 1, 4,   // Top Left facet
      0, 4, 3,   // Top Right facet
      1, 2, 4,   // Bottom Left facet
      4, 2, 3,   // Bottom Right facet
      0, 3, 1,   // Front cross-brace structure
      1, 3, 2    // Lower structural tie
    ];

    g.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    g.setIndex(indices);
    g.computeVertexNormals();
    return g;
  }, []);

  return <primitive object={geom} attach="geometry" />;
};

// --- 2. RIGID WIREFRAME PHYSICS ENGINE (CONTACT SECTOR) ---
const PhysicsMesh = ({ color }) => {
  const size = useMemo(() => Math.random() * 0.6 + 0.5, []);
  const shapeType = useMemo(() => Math.floor(Math.random() * 3), []); 

  const [ref, api] = useSphere(() => ({
    mass: 1,
    args: [size],
    position: [
      (Math.random() - 0.5) * 20, 
      (Math.random() - 0.5) * 12, 
      (Math.random() - 0.5) * 2
    ],
    linearDamping: 0.15,
    angularDamping: 0.08,
  }));

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    api.applyForce([
      Math.sin(t * 0.35 + size) * 0.22, 
      Math.cos(t * 0.22 + size) * 0.18, 
      0
    ], [0, 0, 0]);
  });

  return (
    <mesh ref={ref}>
      {shapeType === 0 && <icosahedronGeometry args={[size, 0]} />}
      {shapeType === 1 && <boxGeometry args={[size * 1.1, size * 1.1, size * 1.1]} />}
      {shapeType === 2 && <KiteGeometry />}
      <meshBasicMaterial 
        color={color} 
        wireframe={true}
        transparent={true}
        opacity={0.35}
      />
    </mesh>
  );
};

const MouseCollider = () => {
  const { viewport } = useThree();
  const [, api] = useSphere(() => ({ type: "Kinematic", args: [2.5] }));
  
  return useFrame((state) => {
    const x = (state.mouse.x * viewport.width) / 2;
    const y = (state.mouse.y * viewport.height) / 2;
    api.position.set(x, y, 0);
  });
};

// --- 3. QUANTUM WAVE & TEXT MORPHING ENGINE (RESPONSIVE MULTI-SCALE) ---
const QuantumHero = ({ onTransitionComplete }) => {
  const mountRef = useRef(null);
  
  useEffect(() => {
    let frameId;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 3000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mountRef.current.appendChild(renderer.domElement);
    
    // Smooth adaptive camera tracking depth based on mobile vs desktop widths
    camera.position.z = window.innerWidth < 768 ? 1300 : 1000;

    const particleCount = 18000;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const initialPositions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = 0; positions[i * 3 + 1] = 0; positions[i * 3 + 2] = 0;
      initialPositions[i * 3] = (Math.random() - 0.5) * 3000;
      initialPositions[i * 3 + 1] = (Math.random() - 0.5) * 3000;
      initialPositions[i * 3 + 2] = (Math.random() - 0.5) * 3000;
      if (Math.random() > 0.6) {
        colors[i * 3] = 0.96; colors[i * 3 + 1] = 0.94; colors[i * 3 + 2] = 0.92;
      } else {
        colors[i * 3] = 0.92; colors[i * 3 + 1] = 0.35; colors[i * 3 + 2] = 0.05;
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({ size: 2.2, vertexColors: true, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending });
    const points = new THREE.Points(geometry, material);
    scene.add(points);

    // Adaptive coordinate sampler calculating device limits dynamically
    const getCoords = (text, size, baseScaleX = 1.3, baseScaleY = 1.3, multiLine = false) => {
      const isMobile = window.innerWidth < 768;
      // Recalculate font metrics dynamically to keep text within safe parameters
      const workingSize = isMobile ? Math.min(size * 0.55, 65) : size;
      const scaleX = isMobile ? baseScaleX * 0.65 : baseScaleX;
      const scaleY = isMobile ? baseScaleY * 0.65 : baseScaleY;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 1600; 
      canvas.height = 400;
      
      ctx.font = `bold ${workingSize}px serif`; 
      ctx.textAlign = 'center'; 
      ctx.textBaseline = 'middle'; 
      ctx.fillStyle = 'white';
      
      if (multiLine) {
        const lines = text.split('\n');
        const lineSpacing = workingSize * 1.3;
        lines.forEach((line, i) => {
          ctx.fillText(line, 800, 200 + (i - (lines.length - 1) / 2) * lineSpacing);
        });
      } else { 
        ctx.fillText(text, 800, 200); 
      }
      
      const data = ctx.getImageData(0, 0, 1600, 400).data;
      const coords = [];
      for (let y = 0; y < 400; y += 4) {
        for (let x = 0; x < 1600; x += 4) {
          if (data[(y * 1600 + x) * 4 + 3] > 128) coords.push({ x: (x - 800) * scaleX, y: (200 - y) * scaleY });
        }
      }
      return coords;
    };

    const states = {
      NAME: getCoords("BARNY N", 180, 1.6, 1.6),
      ABOUT: getCoords("ABOUT ME", 160, 1.4, 1.4),
      ARTIST: getCoords("ARTIST", 160, 1.4, 1.4),
      DESIGNER: getCoords("DESIGNER", 160, 1.4, 1.4),
      LEADER: getCoords("STUDENT LEADER", 120, 1.2, 1.2),
      INNOVATOR: getCoords("INNOVATOR", 150, 1.4, 1.4),
      FAITH: getCoords("I can do all things through Christ\nwho strengthens me.\nPhilippians 4:13", 60, 1.1, 1.1, true)
    };

    let currentPhase = "START";
    let loopIndex = 0;
    const loopOrder = ["ABOUT", "WAVE", "ARTIST", "DNA_WAVE", "DESIGNER", "WAVE", "LEADER", "WAVE", "INNOVATOR", "WAVE", "FAITH", "DNA_WAVE"];

    setTimeout(() => { currentPhase = "NAME"; onTransitionComplete(); }, 500);
    setTimeout(() => {
      currentPhase = "WAVE";
      setInterval(() => {
        currentPhase = loopOrder[loopIndex];
        loopIndex = (loopIndex + 1) % loopOrder.length;
      }, 4500);
    }, 5000);

    const animate = (time) => {
      frameId = requestAnimationFrame(animate);
      const posArray = geometry.attributes.position.array;
      const t = time * 0.001;
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        let tx, ty, tz;

        if (currentPhase === "START") {
          tx = 0; ty = 0; tz = 0;
        } else if (currentPhase === "WAVE") {
          const x = (i / particleCount - 0.5) * 3000;
          const twist = x * 0.0035;
          const radius = 200 * Math.sin(t * 0.35 + x * 0.001);
          tx = x;
          ty = radius * Math.sin(twist + t + (i % 2 === 0 ? 0 : Math.PI)) + Math.sin(t * 1.5 + x * 0.004) * 120;
          tz = radius * Math.cos(twist + t + (i % 2 === 0 ? 0 : Math.PI));
        } else if (currentPhase === "DNA_WAVE") {
          const x = (i / particleCount - 0.5) * 2500;
          const strand = (i % 2 === 0) ? 0 : Math.PI; 
          const twistSpeed = x * 0.004 + t * 1.8;
          tx = x;
          ty = 140 * Math.sin(twistSpeed + strand) + Math.sin(t * 1.2 + x * 0.002) * 60;
          tz = 140 * Math.cos(twistSpeed + strand);
        } else if (states[currentPhase]) {
          const coords = states[currentPhase];
          const c = coords[i % coords.length];
          tx = c.x + Math.sin(t * 1.2 + i) * 4;
          ty = c.y + Math.cos(t * 1.1 + i) * 4;
          tz = (Math.random() - 0.5) * 15 + Math.sin(t * 2 + i * 0.01) * 20;
        } else {
          tx = initialPositions[i3]; ty = initialPositions[i3+1]; tz = initialPositions[i3+2];
        }

        const lerp = (currentPhase === "WAVE" || currentPhase === "DNA_WAVE") ? 0.03 : 0.07;
        posArray[i3] += (tx - posArray[i3]) * lerp;
        posArray[i3+1] += (ty - posArray[i3+1]) * lerp;
        posArray[i3+2] += (tz - posArray[i3+2]) * lerp;
      }
      points.rotation.y += 0.0006;
      geometry.attributes.position.needsUpdate = true;
      renderer.render(scene, camera);
    };
    animate(0);

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.position.z = window.innerWidth < 768 ? 1300 : 1000;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      if (mountRef.current) mountRef.current.removeChild(renderer.domElement);
    };
  }, [onTransitionComplete]);

  return <div ref={mountRef} className="absolute inset-0 z-10 pointer-events-none" />;
};

// --- 4. REFINED ANATOMICAL BUTTERFLY ENGINE ---
const GrowthButterfly = ({ onExplore }) => (
  <div className="absolute inset-0 z-40 pointer-events-none overflow-hidden">
    <style>{`
      @keyframes steadyPath {
        0% { transform: translate(-15vw, 80vh) scale(0.35) rotate(25deg); }
        30% { transform: translate(25vw, 45vh) scale(0.6) rotate(-5deg); }
        60% { transform: translate(60vw, 60vh) scale(0.9) rotate(15deg); }
        100% { transform: translate(115vw, 25vh) scale(1.3) rotate(5deg); }
      }
      @keyframes wingFlapLeft { 0%, 100% { transform: rotateY(0deg); } 50% { transform: rotateY(75deg); } }
      @keyframes wingFlapRight { 0%, 100% { transform: rotateY(0deg); } 50% { transform: rotateY(-75deg); } }
      @keyframes evolutionColors {
        0% { filter: drop-shadow(0 0 10px rgba(120, 53, 15, 0.5)); color: #78350f; }
        40% { filter: drop-shadow(0 0 15px rgba(113, 113, 122, 0.5)); color: #71717a; }
        80% { filter: drop-shadow(0 0 25px rgba(234, 88, 12, 0.6)); color: #ea580c; }
        100% { filter: drop-shadow(0 0 30px rgba(255, 255, 255, 0.8)); color: #ffffff; }
      }
      @keyframes textReveal {
        0% { opacity: 0; transform: translateY(30px); filter: blur(10px); }
        100% { opacity: 1; transform: translateY(0); filter: blur(0); }
      }
      .butterfly-wing-l { animation: wingFlapLeft 0.16s infinite ease-in-out; transform-origin: right center; }
      .butterfly-wing-r { animation: wingFlapRight 0.16s infinite ease-in-out; transform-origin: left center; }
      .growth-butterfly { animation: steadyPath 22s forwards linear, evolutionColors 22s forwards linear; position: absolute; perspective: 1000px; will-change: transform; }
      .gallery-link-reveal { animation: textReveal 2.5s cubic-bezier(0.23, 1, 0.32, 1) forwards; animation-delay: 3.5s; }
    `}</style>

    <div className="growth-butterfly flex items-center justify-center">
      <div className="flex items-center">
        <div className="butterfly-wing-l">
          <svg width="40" height="52" viewBox="0 0 100 150" fill="currentColor">
            <path d="M100,70 C95,30 65,5 35,5 C5,5 0,40 45,70 C10,80 5,125 35,140 C65,155 90,120 100,70 Z" />
          </svg>
        </div>
        <div className="w-[4px] h-[28px] bg-[#1a0f08] rounded-full z-10 mx-[-2px] relative shadow-sm">
          <div className="absolute top-[-6px] left-[-4px] flex gap-[8px]">
            <div className="w-[1px] h-[12px] bg-[#1a0f08] rounded-full origin-bottom rotate-[-28deg]" />
            <div className="w-[1px] h-[12px] bg-[#1a0f08] rounded-full origin-bottom rotate-[28deg]" />
          </div>
        </div>
        <div className="butterfly-wing-r">
          <svg width="40" height="52" viewBox="0 0 100 150" fill="currentColor">
            <path d="M0,70 C5,30 35,5 65,5 C95,5 100,40 55,70 C90,80 95,125 65,140 C35,155 10,120 0,70 Z" />
          </svg>
        </div>
      </div>
    </div>

    <div className="absolute inset-0 flex items-center justify-center pointer-events-auto">
      <button onClick={onExplore} className="gallery-link-reveal opacity-0 group flex flex-col items-center gap-2 cursor-pointer mt-[60vh] px-4">
        <span className="text-[9px] tracking-[0.6em] text-orange-500/80 font-bold uppercase mb-1">Curation</span>
        <h3 className="text-white/90 text-2xl sm:text-3xl md:text-4xl font-display italic tracking-tight hover:text-orange-500 transition-all duration-500 flex items-center gap-3">
          Enter the Gallery <ChevronRight className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
        </h3>
        <div className="w-4 group-hover:w-28 h-[1px] bg-orange-500/40 group-hover:bg-orange-500 transition-all duration-1000" />
      </button>
    </div>
  </div>
);

// --- 5. ARTWORK MATRIX DATA ---
const artwork = [
  { id: 1, title: "1", subtitle: "colorful tales", desc: "", img: "/images/colorfultale2.png" },
  { id: 2, title: "2", subtitle: "Nostalgia", desc: "", img: "/images/friendsplants.jpg.jpeg" },
  { id: 3, title: "3", subtitle: "G-Eazy", desc: "", img: "/images/G.E 4.png" },
  { id: 4, title: "4", subtitle: "Moment of Disposition", desc: "", img: "/images/orangesky.png" },
  { id: 5, title: "5", subtitle: "BIG-Black Is Gold", desc: "", img: "/images/leo4.png" },
  { id: 6, title: "6", subtitle: "Man in The News", desc: "", img: "/images/S.W.png" }
];

export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [transitioned, setTransitioned] = useState(false);
  const [lightUp, setLightUp] = useState(false);

  // ---> ADD YOUR NEW IMAGES RIGHT HERE INSIDE THIS ARRAY <---
  const backgroundImages = [
    "/images/legendary.png", 
    "/images/bg1.jpg",       
    "/images/bg2.jpg",       
    "/images/bg3.jpg",
    "/images/bg4.jpg"
    
  ];
  
  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLightUp(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-[#f5f1ed] text-[#2a1a10] overflow-x-hidden antialiased select-none">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@700&family=Instrument+Serif:ital@0;1&family=Plus+Jakarta+Sans:wght@400;700&display=swap');
        .font-display { font-family: 'Instrument Serif', serif; }
        .font-heading { font-family: 'Cinzel', serif; }
        .glass-nav { background: rgba(245, 241, 237, 0.85); backdrop-filter: blur(20px); border: 1px solid rgba(42, 26, 16, 0.08); }
        .skill-badge { background: #ffffff; padding: 1.75rem; border-radius: 1.5rem; border: 1px solid rgba(42, 26, 16, 0.04); transition: transform 0.4s cubic-bezier(0.2, 1, 0.3, 1); }
        .skill-badge:hover { transform: translateY(-4px); }
      `}</style>

      {/* Navigation Layer - Fully Responsive Touch Spacing */}
      <nav className={`fixed top-4 sm:top-8 left-1/2 -translate-x-1/2 w-[92%] max-w-5xl z-[100] px-4 sm:px-8 py-4 sm:py-5 rounded-[1.5rem] sm:rounded-[2rem] glass-nav transition-all duration-500 ${scrolled ? 'scale-95 shadow-lg' : ''}`}>
        <div className="flex justify-between items-center">
          <div className="text-xl sm:text-2xl font-heading font-bold">B<span className="text-orange-600">N</span></div>
          <div className="flex gap-4 sm:gap-10 text-[9px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold">
            <button onClick={() => scrollTo('about')} className="hover:text-orange-600 cursor-pointer transition-colors">Origins</button>
            <button onClick={() => scrollTo('gallery')} className="hover:text-orange-600 cursor-pointer transition-colors">Portfolio</button>
            <button onClick={() => scrollTo('contact')} className="hover:text-orange-600 cursor-pointer transition-colors">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section Interface with Responsive Multi-Layer Scaling */}
      <section className="relative h-screen w-full bg-black flex items-center justify-center overflow-hidden">
        {/* BACKGROUND LAYER 1: Ambient Backdrop blur */}
        <div 
          className={`absolute inset-0 z-0 bg-cover bg-center bg-no-repeat bg-blend-multiply blur-2xl opacity-30 transition-all duration-[6000ms] ease-out ${
            lightUp ? 'brightness-40 scale-110' : 'brightness-0 scale-100'
          }`}
          style={{ backgroundImage: "url('/images/legendary.png')" }}
        />

        {/* BACKGROUND LAYER 2: Main Piece Frame (Preserves center artwork context on mobile fields) */}
        <div 
          className={`absolute w-[85%] h-[75%] sm:h-[85%] z-0 bg-contain bg-center bg-no-repeat transition-all duration-[6000ms] ease-out ${
            lightUp ? 'opacity-25 scale-100' : 'opacity-0 scale-95'
          }`}
          style={{ backgroundImage: "url('/images/legendary.png')" }}
        />

        <div className="absolute inset-0 z-10 bg-black/50 pointer-events-none mix-blend-multiply" />
        <QuantumHero onTransitionComplete={() => setTransitioned(true)} />
        {transitioned && <GrowthButterfly onExplore={() => scrollTo('gallery')} />}
      </section>

      {/* Six Masterpiece Showcase Gallery - Fixed Cropping Structure */}
      <section id="gallery" className="py-24 sm:py-40 px-4 sm:px-6 max-w-7xl mx-auto">
        <h2 className="text-4xl sm:text-6xl md:text-7xl font-display italic text-center mb-16 sm:mb-32 px-2">
          My Gallery Showcase of the Month
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-16">
          {artwork.map((item) => (
            <div key={item.id} className="group cursor-pointer flex flex-col h-full">
              {/* Aspect Ratio Box with Containment to Prevent Unwanted Cropping */}
              <div className="aspect-[3/4] w-full overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] mb-5 bg-stone-100/60 shadow-sm border border-stone-200/40 flex items-center justify-center p-2 sm:p-4">
                <img 
                  src={item.img} 
                  className="max-w-full max-h-full w-auto h-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-700 ease-out transform group-hover:scale-[1.02]" 
                  alt={`Artwork structural rendering titled ${item.title}`} 
                />
              </div>
              <h3 className="text-xl sm:text-2xl font-display italic text-[#2a1a10]">{item.title}</h3>
              <p className="text-[9px] tracking-widest text-orange-600 uppercase font-bold mb-1">{item.subtitle}</p>
              <p className="text-xs sm:text-sm opacity-70 leading-relaxed text-[#5c4a3e]">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Studio Origins Block */}
      <section id="about" className="py-20 sm:py-36 px-4 sm:px-8 max-w-7xl mx-auto bg-stone-50 rounded-[2.5rem] sm:rounded-[4rem] mb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-24 items-center">
          <div className="space-y-8 sm:space-y-12">
            <h2 className="text-4xl sm:text-6xl font-display italic leading-tight">
              I am an Artist, <br/><span className="text-orange-600"> & Designer.</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="skill-badge">
                <Cpu className="text-orange-600 w-5 h-5 mb-4" />
                <h5 className="font-bold text-sm mb-1">Product Design</h5>
                <p className="text-xs text-[#8b5e3c]">Crafting systemic solutions.</p>
              </div>
              <div className="skill-badge">
                <Palette className="text-orange-600 w-5 h-5 mb-4" />
                <h5 className="font-bold text-sm mb-1">Graphic Design</h5>
                <p className="text-xs text-[#8b5e3c]">Visual brand architecture.</p>
              </div>
              <div className="skill-badge">
                <MousePointer2 className="text-orange-600 w-5 h-5 mb-4" />
                <h5 className="font-bold text-sm mb-1">Illustrations</h5>
                <p className="text-xs text-[#8b5e3c]">Custom micro digital assets.</p>
              </div>
              <div className="skill-badge">
                <Wind className="text-orange-600 w-5 h-5 mb-4" />
                <h5 className="font-bold text-sm mb-1">System Design</h5>
                <p className="text-xs text-[#8b5e3c]">Innovation & optimization.</p>
              </div>
            </div>
          </div>
          
          {/* About Frame Component */}
          <div className="group cursor-pointer w-full">
            <div className="rounded-[2rem] sm:rounded-[3rem] overflow-hidden h-[400px] sm:h-[600px] border-4 sm:border-8 border-white shadow-xl bg-stone-200">
              <img 
                src="/images/Me3.JPG" 
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 scale-105 group-hover:scale-100" 
                alt="Creative Design Lab Portfolio Portrait" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section Hub */}
      <section id="contact" className="relative py-24 sm:py-40 px-4 sm:px-6 min-h-screen flex items-center justify-center overflow-hidden bg-white">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <Canvas camera={{ position: [0, 0, 15] }} className="pointer-events-auto">
            <ambientLight intensity={1.5} />
            <Physics gravity={[0, 0, 0]}>
              <MouseCollider />
              {[...Array(window.innerWidth < 768 ? 20 : 45)].map((_, i) => (
                <PhysicsMesh key={i} color={i % 2 === 0 ? "#ea580c" : "#2a1a10"} />
              ))}
            </Physics>
          </Canvas>
        </div>

        {/* Contact Info Container Card */}
        <div className="relative z-10 w-full max-w-5xl bg-gradient-to-b from-[#4a3525]/95 via-[#3a281c]/95 to-[#1c120c]/95 backdrop-blur-xl rounded-[2.5rem] sm:rounded-[4rem] p-8 sm:p-16 md:p-24 text-center text-white shadow-2xl border border-white/10">
          <h2 className="text-4xl sm:text-6xl md:text-8xl font-display italic mb-8 sm:mb-12 text-white/95">Contact Me</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 text-left mb-12 sm:mb-16 border-t border-white/10 pt-12 sm:pt-16">
            <div>
              <p className="text-[9px] tracking-widest uppercase opacity-50 mb-3 font-bold text-orange-400">Inquiries</p>
              <a href="mailto:info@barny.co.ke" className="text-xl sm:text-2xl hover:text-orange-400 transition-colors font-display italic font-medium text-white/90break-all">
                Email Me
              </a>
            </div>
            <div>
              <p className="text-[9px] tracking-widest uppercase opacity-50 mb-3 font-bold text-orange-400">Digital Network</p>
              <div className="flex gap-6 text-white/90">
                <a href="https://instagram.com/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
                  <span className="sr-only">Instagram Profile</span>
                  <Instagram size={20} />
                </a>
                <a href="https://www.linkedin.com/in/barny-n/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
                  <span className="sr-only">LinkedIn Profile</span>
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>
         <a 
            href="mailto:info@barny.co.ke?subject=Inquiry%20from%20Portfolio" 
            className="inline-flex items-center gap-3 mx-auto px-8 sm:px-12 py-4 sm:py-6 bg-white text-[#2a1a10] rounded-full text-[9px] sm:text-[10px] font-bold tracking-[0.4em] sm:tracking-[0.5em] hover:bg-orange-500 hover:text-white transition-all uppercase cursor-pointer shadow-md"
          >
            Send Inquiry <Send size={11} />
          </a>

        </div>
      </section>

     {/* Production Footer Layout */}
<footer className="py-12 sm:py-20 px-6 sm:px-8 border-t border-[#d6ccc2] bg-white">
  <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-6 sm:gap-10">
    
    {/* Left Side: Branding & Copyright */}
    <div className="flex flex-col gap-1 items-center sm:items-start text-center sm:text-left">
      <p className="text-xl font-heading font-bold">B<span className="text-orange-600">N</span></p>
      <p className="text-[#8b5e3c] text-xs tracking-widest font-medium">Barny N. © 2026 • All Rights Reserved</p>
    </div>

    {/* Right Side: Marcus Aurelius Quote */}
    <div className="text-center sm:text-right max-w-sm sm:ml-auto">
      <p className="text-sm font-display italic text-[#5c4a3e] tracking-wide leading-relaxed">
        <em>"Uno smeraldo brilla altrettanto se la sua lode non è detta."</em>
      </p>
      
    </div>

  </div>
</footer>
    </div>
  );
}

